import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, ArrowUpRight } from 'lucide-react';

const LOGO_URL = '/images/logo-done.png';

export function Footer() {
  const { i18n } = useTranslation();
  
  const getText = (fr, ar, en) => {
    if (i18n.language === 'fr') return fr;
    if (i18n.language === 'ar') return ar;
    return en;
  };

  const footerLinks = {
    services: [
      { label: getText('Accessoires Mobiles', 'إكسسوارات الهواتف', 'Mobile Accessories'), path: '/accessoires' },
      { label: getText('Produits Télécom', 'منتجات الاتصالات', 'Telecom Products'), path: '/telecom' },
      { label: getText('Retail Tech', 'تقنية التجزئة', 'Retail Tech'), path: '/bureau-etude' },
    ],
    groupe: [
      { label: getText('Le Groupe', 'المجموعة', 'The Group'), path: '/groupe' },
      { label: getText('Écosystème', 'النظام البيئي', 'Ecosystem'), path: '/ecosysteme' },
      { label: getText('Contact', 'اتصل بنا', 'Contact'), path: '/contact' },
    ],
    ecosystem: [
      { label: 'epaas.io', href: 'https://epaas.io', external: true },
      { label: 'interfracom.com', href: 'https://interfracom.com', external: true },
    ],
  };
  
  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container-main py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={LOGO_URL} 
                alt="D.One" 
                className="h-9 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-white/50 text-sm leading-relaxed max-w-sm mb-6">
              {getText(
                'Groupe agile de conception, sourcing, industrialisation et déploiement de solutions télécom & retail tech.',
                'مجموعة مرنة لتصميم وتوريد وتصنيع ونشر حلول الاتصالات وتقنية البيع بالتجزئة.',
                'Agile group for design, sourcing, industrialization and deployment of telecom & retail tech solutions.'
              )}
            </p>
            
            {/* French Tech Logo */}
            <div className="mb-8">
              <img 
                src="/images/french-tech-logo.png" 
                alt="La French Tech" 
                className="h-14 w-auto opacity-80 hover:opacity-100 transition-opacity"
              />
            </div>
            
            {/* Contact Info */}
            <div className="flex flex-col gap-4">
              <a 
                href="mailto:contact@simplyd.one" 
                className="flex items-center gap-3 text-white/50 hover:text-[#f4b00f] transition-colors text-sm group"
              >
                <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-[#f4b00f] group-hover:bg-[#f4b00f]/10 transition-all">
                  <Mail className="w-4 h-4" />
                </div>
                <span>contact@simplyd.one</span>
              </a>
              <a 
                href="tel:+33491918176" 
                className="flex items-center gap-3 text-white/50 hover:text-[#f4b00f] transition-colors text-sm group"
              >
                <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-[#f4b00f] group-hover:bg-[#f4b00f]/10 transition-all">
                  <span className="text-xs">📞</span>
                </div>
                <span>+33 4 91 91 81 76</span>
              </a>
              <div className="flex items-start gap-3 text-white/50 text-sm">
                <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="text-white/40 text-xs leading-relaxed">
                  <span className="text-[#f4b00f] font-medium">{getText('Siège', 'المقر', 'HQ')}</span>: 30A Allée de la Palun, 13700 Marignane
                </div>
              </div>
            </div>
          </div>
          
          {/* Services */}
          <div className="lg:col-span-2 lg:col-start-6">
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#f4b00f] mb-6">
              {getText('Services', 'الخدمات', 'Services')}
            </h4>
            <ul className="space-y-4">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-white/50 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Groupe */}
          <div className="lg:col-span-2">
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#f4b00f] mb-6">
              {getText('Groupe', 'المجموعة', 'Group')}
            </h4>
            <ul className="space-y-4">
              {footerLinks.groupe.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-white/50 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Ecosystem */}
          <div className="lg:col-span-2">
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#f4b00f] mb-6">
              {getText('Écosystème', 'النظام البيئي', 'Ecosystem')}
            </h4>
            <ul className="space-y-4">
              {footerLinks.ecosystem.map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/50 hover:text-white transition-colors text-sm inline-flex items-center gap-2 group"
                  >
                    {link.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-main py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">
            © 2026 D.One SAS. {getText('Tous droits réservés', 'جميع الحقوق محفوظة', 'All rights reserved')}.
          </p>
          <div className="flex items-center gap-8">
            <Link to="/mentions-legales" className="text-white/30 hover:text-white/60 text-xs transition-colors">
              {getText('Mentions légales', 'الشروط القانونية', 'Legal notice')}
            </Link>
            <Link to="/confidentialite" className="text-white/30 hover:text-white/60 text-xs transition-colors">
              {getText('Confidentialité', 'الخصوصية', 'Privacy')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
